import type { BudgetGoal, BudgetLine, BudgetSummary, Category, TransactionType, UpdateCategoryData } from '@/lib/api/types';
import { BUDGET_GOALS_TOTAL_HUNDREDTHS, BUDGET_TREATMENTS, type BudgetTreatment, type BudgetType } from '@/lib/constants';

// spend, refund, earmark and deduction act on one bucket; income and excluded must not carry one.
export const treatmentRequiresType = (treatment: BudgetTreatment): boolean =>
  treatment === 'spend' || treatment === 'refund' || treatment === 'earmark' || treatment === 'deduction';

// income, refund and earmark describe money arriving, so they belong to income categories;
// spend and deduction describe money leaving, so they belong to expense categories. excluded
// fits either — a transfer has a leg in each direction.
const BUDGET_TREATMENT_DIRECTION: Record<BudgetTreatment, TransactionType | null> = {
  income: 'income',
  refund: 'income',
  earmark: 'income',
  spend: 'expense',
  deduction: 'expense',
  excluded: null,
};

export const treatmentsForCategoryType = (categoryType: TransactionType): BudgetTreatment[] =>
  BUDGET_TREATMENTS.filter((treatment) => {
    const direction = BUDGET_TREATMENT_DIRECTION[treatment];
    return direction === null || direction === categoryType;
  });

export type AssignmentState = 'unassigned' | 'assigned' | 'unsupported';

// An absent budget_treatment is unassigned: the API omits both budget keys together, it never sends "".
// A value this build doesn't recognize makes the pair unreadable ('unsupported', see knownOr) — neither
// unassigned nor safe to overwrite, so the pending filter and its count skip it.
export const assignmentState = (category: Pick<Category, 'budget_treatment' | 'budget_type'>): AssignmentState => {
  if (category.budget_treatment === 'unsupported' || category.budget_type === 'unsupported') return 'unsupported';
  return category.budget_treatment === undefined ? 'unassigned' : 'assigned';
};

// The pair a category carries, with an unrecognized value read as absent. Callers that must tell the two
// apart ask assignmentState first.
export const committedAssignment = (
  category: Pick<Category, 'budget_treatment' | 'budget_type'>
): { treatment: BudgetTreatment | undefined; bucket: BudgetType | undefined } => ({
  treatment: category.budget_treatment === 'unsupported' ? undefined : category.budget_treatment,
  bucket: category.budget_type === 'unsupported' ? undefined : category.budget_type,
});

// What a treatment pill offers: a treatment, or clearing the assignment.
export type AssignmentPick = BudgetTreatment | 'unassigned';

// Both keys always travel together (TRD decision 8): the server reads them as one value object, and
// sending half re-derives an assignment from state the client didn't look at. '' clears a key.
export type AssignmentPatch = Required<Pick<UpdateCategoryData, 'budget_treatment' | 'budget_type'>>;

export type TreatmentPickResult =
  | { kind: 'save'; patch: AssignmentPatch }
  | { kind: 'needs-bucket'; treatment: BudgetTreatment };

// A row saves the moment its pair is legal (§11). A treatment that takes no bucket is legal on its own;
// one that does is legal only with a bucket — the one the row already shows (spend <-> deduction,
// refund <-> earmark), otherwise it waits for the user to pick one.
export const resolveTreatmentPick = (pick: AssignmentPick, shownBucket: BudgetType | ''): TreatmentPickResult => {
  if (pick === 'unassigned') return { kind: 'save', patch: { budget_treatment: '', budget_type: '' } };
  if (!treatmentRequiresType(pick)) return { kind: 'save', patch: { budget_treatment: pick, budget_type: '' } };
  if (shownBucket) return { kind: 'save', patch: { budget_treatment: pick, budget_type: shownBucket } };
  return { kind: 'needs-bucket', treatment: pick };
};

export const resolveBucketPick = (treatment: BudgetTreatment, bucket: BudgetType): AssignmentPatch => ({
  budget_treatment: treatment,
  budget_type: bucket,
});

export type LineState =
  | { kind: 'within'; usedPercent: number }
  | { kind: 'over'; usedPercent: number }
  | { kind: 'refunded'; spent: number }
  | { kind: 'idle' }
  | { kind: 'unbounded'; spent: number }
  | { kind: 'negative-allowance'; mustSpend: number };

// See docs/specs/budgets/budgets-trd.md §9. `used` is only ever read as a percentage in the
// within/over cases — everywhere else must_spend is zero or negative and the ratio is a
// zero-divisor artifact, not a measurement.
export const lineState = (line: BudgetLine): LineState => {
  const mustSpend = parseFloat(line.must_spend);
  const spent = parseFloat(line.spent);

  if (mustSpend > 0) {
    if (spent < 0) return { kind: 'refunded', spent };
    if (spent > mustSpend) return { kind: 'over', usedPercent: parseFloat(line.used) };
    return { kind: 'within', usedPercent: parseFloat(line.used) };
  }

  if (mustSpend === 0) {
    // Not one of the TRD's five rows verbatim: a refund landing on a bucket with no allowance
    // yet. Grouped with the must_spend>0 case above (refunded), not with negative-allowance
    // below, since must_spend itself isn't negative here.
    if (spent < 0) return { kind: 'refunded', spent };
    if (spent > 0) return { kind: 'unbounded', spent };
    return { kind: 'idle' };
  }

  // must_spend < 0
  if (spent > 0) return { kind: 'unbounded', spent };
  return { kind: 'negative-allowance', mustSpend };
};

// A bucket that has spent past what it may spend: over its limit, or spending where there is no limit at all
// (unbounded). The two are the rows that carry the expense dot, and the ones the verdict counts. A count of rows
// by state, not arithmetic on money — every amount behind it is the API's.
export const isPastLimit = (line: BudgetLine): boolean => {
  const { kind } = lineState(line);
  return kind === 'over' || kind === 'unbounded';
};

export const countPastLimit = (lines: BudgetLine[]): number => lines.filter(isPastLimit).length;

export type BudgetVerdict =
  /** Nothing classified yet: the budget hasn't started. */
  | { kind: 'first-run' }
  /** No limit this month (no income classified): no percentage, and "past the limit" would mean every spend. */
  | { kind: 'no-limit' }
  | { kind: 'reading'; overallUsed: string; pastLimit: number };

// Painel's one-sentence answer to "where do I stand": read from the summary the section below renders in full.
export const budgetVerdict = (summary: BudgetSummary | undefined, categories: Category[]): BudgetVerdict | null => {
  // No categories at all: nothing to classify yet, and the section below already says where to start.
  if (categories.length === 0) return null;
  if (categories.every((c) => assignmentState(c) === 'unassigned')) return { kind: 'first-run' };
  if (!summary) return null;
  // The same rule as the Utilizado figure (§9): with no total limit the API's 0.00 is a convention.
  if (!(parseFloat(summary.total_must_spend) > 0)) return { kind: 'no-limit' };
  return { kind: 'reading', overallUsed: summary.overall_used, pastLimit: countPastLimit(summary.lines) };
};

// Percentages arrive as decimal strings ("33.33"); summing them as floats risks the classic
// 0.1 + 0.2 drift. Round each to hundredths first so the sum-to-100 gate compares exact integers.
export const toHundredths = (percentage: string): number => Math.round(parseFloat(percentage) * 100);

export const sumHundredths = (goals: { percentage: string }[]): number =>
  goals.reduce((sum, goal) => sum + toHundredths(goal.percentage), 0);

// The other way round, for a request body: whole hundredths back to the two-decimal string the API
// canonicalizes to ("4500" -> "45.00"). Integer arithmetic only, so no float can leak into the payload.
export const fromHundredths = (hundredths: number): string => {
  const abs = Math.abs(hundredths);
  return `${hundredths < 0 ? '-' : ''}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
};

export type GoalsBalance =
  | { kind: 'complete' }
  | { kind: 'short'; hundredths: number }
  | { kind: 'over'; hundredths: number };

// Where a set of goals stands against the sum-to-100 rule (§10.3), from its total in hundredths — how far
// short or over, so the page can say "Faltam 5%" instead of only refusing to save.
export const goalsBalance = (total: number): GoalsBalance => {
  if (total === BUDGET_GOALS_TOTAL_HUNDREDTHS) return { kind: 'complete' };
  return total < BUDGET_GOALS_TOTAL_HUNDREDTHS
    ? { kind: 'short', hundredths: BUDGET_GOALS_TOTAL_HUNDREDTHS - total }
    : { kind: 'over', hundredths: total - BUDGET_GOALS_TOTAL_HUNDREDTHS };
};

// The goals page edits an overlay on the saved set: only the buckets the user moved, in hundredths. An entry
// equal to its saved value is not an edit, so the overlay is empty exactly when there is nothing to save, and
// a slider dragged back to where it started is clean again (§10.6: "Descartar" reverts to the saved set).
// The one-press way to close a draft that doesn't add up: move the largest group (other than the one the user
// just moved — that edit is the point) by exactly what is missing or over. Short: the largest takes the gap, and
// can't pass 100 since every group is at most the total. Over: the largest that can give it up without going
// below 0. Integer hundredths only; the server re-validates the sum anyway.
export const closingAdjustment = (
  draft: BudgetGoal[],
  exclude?: BudgetType
): { type: BudgetType; hundredths: number } | null => {
  const gap = BUDGET_GOALS_TOTAL_HUNDREDTHS - sumHundredths(draft);
  if (gap === 0) return null;
  const candidates = draft
    .filter((goal) => goal.type !== exclude)
    .map((goal) => ({ type: goal.type, hundredths: toHundredths(goal.percentage) }))
    .filter((goal) => goal.hundredths + gap >= 0)
    .sort((a, b) => b.hundredths - a.hundredths);
  const pick = candidates[0];
  return pick ? { type: pick.type, hundredths: pick.hundredths + gap } : null;
};

export type GoalEdits = Partial<Record<BudgetType, number>>;

export const setGoalEdit = (edits: GoalEdits, saved: BudgetGoal[], type: BudgetType, hundredths: number): GoalEdits => {
  const next = { ...edits };
  const savedGoal = saved.find((goal) => goal.type === type);
  if (savedGoal !== undefined && toHundredths(savedGoal.percentage) === hundredths) delete next[type];
  else next[type] = hundredths;
  return next;
};

// When the saved set changes underneath the overlay — the response to a save, or a refetch — an edit that now
// equals the new saved value has been applied (or overtaken) and stops being one.
export const pruneGoalEdits = (edits: GoalEdits, saved: BudgetGoal[]): GoalEdits => {
  const next: GoalEdits = {};
  for (const goal of saved) {
    const edit = edits[goal.type];
    if (edit !== undefined && edit !== toHundredths(goal.percentage)) next[goal.type] = edit;
  }
  return next;
};

// The set as it would be saved: the API's own strings for what the user left alone (a "33.33" saved elsewhere
// goes back as "33.33"), the canonical rendering of the edit for what they moved. Same order as the API's.
export const draftGoals = (saved: BudgetGoal[], edits: GoalEdits): BudgetGoal[] =>
  saved.map((goal) => {
    const edit = edits[goal.type];
    return edit === undefined ? goal : { type: goal.type, percentage: fromHundredths(edit) };
  });
